import type { TunaEditor } from './plugin-types.js'

/**
 * Registry of mounted editors keyed by their root entity id, populated by
 * `element-editor` on mount/unmount.
 *
 * Surfaces OUTSIDE a document's `TunaEditorContext` — notably the agent
 * sidebar's proposal panel (`ProposalsHeader`), which is a sibling region
 * of the editor, not a descendant — cannot read the live
 * editor via `useEditor`. This registry lets them resolve the editor for a
 * proposal's target entity so Accept can actually apply the patch. When the
 * target document is not open, `getLiveEditor` returns undefined and callers
 * degrade gracefully ("open the document to apply").
 */
const liveEditors = new Map<string, Set<TunaEditor>>()

/** Register the live editor for an entity id (call on editor mount). */
export const registerLiveEditor = (entityId: string, editor: TunaEditor): void => {
  const editors = liveEditors.get(entityId) ?? new Set<TunaEditor>()
  /** Re-registering moves the editor to the end, making it the most recent again */
  editors.delete(editor)
  editors.add(editor)
  liveEditors.set(entityId, editors)
}

/**
 * Unregister on unmount. Removes only THIS editor, so another editor that is still mounted
 * for the same entity stays resolvable. A single slot per entity used to drop the entry when
 * the later of two mounts unmounted (GH #1915), and a remount's newer editor must likewise
 * survive the old one's cleanup (React StrictMode / fast nav).
 */
export const unregisterLiveEditor = (entityId: string, editor: TunaEditor): void => {
  const editors = liveEditors.get(entityId)
  if (!editors) {
    return
  }
  editors.delete(editor)
  if (editors.size === 0) {
    liveEditors.delete(entityId)
  }
}

/**
 * The most recently mounted live editor for an entity id, or undefined when the doc isn't
 * mounted.
 */
export const getLiveEditor = (entityId: string): TunaEditor | undefined => {
  const editors = liveEditors.get(entityId)
  return editors ? [...editors].at(-1) : undefined
}

/** Test-only: clear the registry between tests. */
export const resetLiveEditorRegistryForTests = (): void => {
  liveEditors.clear()
}
